import { useCallback, useMemo } from 'react';
import { IElement } from '@shared/models/project.model';
import {
  getConversionFactorValue,
  SupportedConversionUnits,
} from '@shared/helpers/conversion-factors.helpers';
import { Results } from '@shared/models/unit.model';
import {
  useGetResultsPerGFA,
  useVersionResultRecord,
} from '@features/results/use-results.hooks';
import { useProposalsResultRecords } from '@features/proposals/use-proposals.hooks';
import { SbefGroupCode } from '@shared/construction/sbef.model';
import { SBEFCustomGroup } from '@shared/construction/sbef-custom-group.model';
import { IProposal } from '@shared/models/proposals.model';
import { useSelectedVersion } from '@client/store/ui/ui.hook';
import { createSbefGroupingHelpers } from '@features/elements/helpers/element-grouping.helpers';
import { buildSbefRootGroupAggregatesByScenarioId } from '@shared/helpers/group-section-bar-scale.helpers';
import { filterListElementsContributingToVersionTotals } from '@shared/helpers/element-list-group-contributors.helpers';
import { getProposal } from '@shared/helpers/proposals/proposals.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import { scaleResultsByVersionContentCoverage } from '@shared/helpers/results/content-coverage.helpers';
import { groupBy } from 'lodash';

import {
  DEFAULT_REPORT_EXCLUDED_SBEF_GROUP_CODES,
  hasPositiveSbefGroupCo2e,
} from './report-dashboard.helpers';

export type ISBEFElementGroup = {
  /** SBEF group key (code or custom group id). */
  groupKey: string;
  /**
   * The SBEF group label (code + description)
   */
  label: string;
  /**
   * Root element ids in this group that contribute to version totals — same
   * set as {@link SbefGroupRow} / {@link useGroupSectionProps}.
   */
  itemIds: string[];
  /**
   * Sum of group results per GFA for the requested scenario (baseline or proposal).
   */
  sums: Results;
};

/**
 * SBEF group totals for report pages. Root-level elements are summed the same
 * way as {@link buildSbefRootGroupAggregatesByScenarioId}, then scaled by
 * version Content coverage so per-m² values match Build-view proposal totals.
 *
 * @param excludeGroupCodes - SBEF group codes to exclude from the report.
 */
export const useGetSBEFGroupResultsByProposal = (options?: {
  excludeGroupCodes?: SbefGroupCode[];
  customGroups?: SBEFCustomGroup[];
}): ((proposalId: IProposal['id']) => ISBEFElementGroup[]) => {
  const excludeGroupsCodesSet = useMemo(
    () =>
      new Set<string>(
        options?.excludeGroupCodes ?? DEFAULT_REPORT_EXCLUDED_SBEF_GROUP_CODES,
      ),
    [options?.excludeGroupCodes],
  );

  const getResultsPerGFA = useGetResultsPerGFA();
  const version = useSelectedVersion();
  const proposalRecords = useProposalsResultRecords();
  const baselineResultRecord = useVersionResultRecord(version);
  const grouping = useMemo(
    () => createSbefGroupingHelpers(options?.customGroups ?? []),
    [options?.customGroups],
  );

  return useCallback(
    (proposalId: IProposal['id']) => {
      if (!version) return [];

      const isBaselineScenario = proposalId === version.id;
      const proposal = isBaselineScenario
        ? undefined
        : getProposal(version, proposalId);
      const scenarioVersion = isBaselineScenario
        ? version
        : getResolvedBuildingVersion(version, proposalId);

      const proposalRecordsByProposalId = { ...proposalRecords };
      delete proposalRecordsByProposalId[version.id];

      const byScenario = buildSbefRootGroupAggregatesByScenarioId({
        version,
        baselineRecord: baselineResultRecord,
        proposalRecordsByProposalId,
        proposals: proposal ? [proposal] : [],
        getGroupKey: grouping.getGroupKey,
      });

      const rows = isBaselineScenario
        ? (byScenario[version.id] ?? [])
        : (byScenario[proposalId] ?? []);

      const contributingRoots = filterListElementsContributingToVersionTotals(
        version.elements.filter(isElement),
        version,
      );
      const rootsByGroupKey = groupBy(contributingRoots, grouping.getGroupKey);

      const orderedKeys = grouping.orderKeys(rows.map((row) => row.groupKey));

      return orderedKeys.flatMap((groupKey) => {
        if (excludeGroupsCodesSet.has(groupKey)) return [];
        const row = rows.find((r) => r.groupKey === groupKey);
        if (!row) return [];
        const itemIds = (rootsByGroupKey[groupKey] ?? [])
          .filter(isElement)
          .map((el: IElement) => el.id);
        // Root-element sums omit version Content coverage; scale to match Build bars.
        const sums = getResultsPerGFA(
          scaleResultsByVersionContentCoverage(row.resultsSum, scenarioVersion),
        );
        if (!hasPositiveSbefGroupCo2e(sums)) return [];
        return [
          {
            groupKey,
            label: grouping.getGroupLabel(groupKey),
            itemIds,
            sums,
          },
        ];
      });
    },
    [
      version,
      proposalRecords,
      baselineResultRecord,
      grouping,
      excludeGroupsCodesSet,
      getResultsPerGFA,
    ],
  );
};

/**
 * Get the max value of all SBEF groups for a given unit.
 */
export const useSBEFGroupsMaxValue = (
  groups: ISBEFElementGroup[],
  unit: SupportedConversionUnits,
) => {
  const maxValues = groups.map((g) => getConversionFactorValue(g.sums, unit));
  return Math.max(...maxValues, 0);
};
